import React, { useContext, useEffect, useState } from 'react'
import { IonText, useIonModal } from '@ionic/react'
import { CustomSpacer, PagingContent, ParamOption, TextStyle } from '../../../components'
import styled from 'styled-components'
import moment from 'moment-timezone'
import { observer } from 'mobx-react'
import { StoresContext } from '../../../provider/StoreProvider'
import { TradeResultConfirmModal } from '../modals/TradeResultConfirmModal'
import { onParams, PARAM_SORT, PARAM_FILTER, HistoryList } from '../../../common'

export const AssetHistory: React.FC = observer(() => {
  const [sortType, setSortType] = useState<PARAM_SORT>(PARAM_SORT.LATEST)
  const [filterType, setFilterType] = useState(PARAM_FILTER.ALL_LOW)

  const [tradeResult, setTradeResult] = useState<HistoryList[]>()
  const { myStore } = useContext(StoresContext)
  const pagingStore = myStore.assetHistoryPagingStore

  useEffect(() => {
    pagingStore.load(undefined, true)
  }, [])

  useEffect(() => {
    onParams({
      store: pagingStore,
      sortType: sortType,
      filterType: filterType,
      filterName: 'purchaseType',
    })
  }, [sortType, filterType])

  const [txDetailPresent, txDetailDismiss] = useIonModal(TradeResultConfirmModal, {
    tradeResult: tradeResult,
    onDismiss: () => {
      txDetailDismiss()
    },
  })

  const onTxDetailModal = (tradeResult: HistoryList[]) => {
    setTradeResult(tradeResult)
    txDetailPresent()
  }

  const filterList = [
    {
      type: PARAM_FILTER.ALL_LOW,
      title: '전체',
    },
    {
      type: PARAM_FILTER.SALE_LOW,
      title: '조각 구매',
    },
    {
      type: PARAM_FILTER.TRADE_BUY,
      title: '트레이드(매수)',
    },
    {
      type: PARAM_FILTER.TRADE_SELL,
      title: '트레이드(매도)',
    },
    {
      type: PARAM_FILTER.TRANSFER,
      title: '지갑 이전',
    },
    {
      type: PARAM_FILTER.DESTRUCT,
      title: '조각 소각',
    },
    {
      type: PARAM_FILTER.REFUND_LOW,
      title: '환불',
    },
  ]

  const sortList = [
    {
      type: 'LATEST',
      title: '최근 거래순',
    },
    {
      type: 'END_DATE',
      title: '오래된 거래순',
    },
  ]

  return (
    <>
      <ParamOption
        slideFilterData={{
          filterList: filterList,
          filterType: filterType,
          setFilterType: setFilterType,
        }}
        sortData={{
          sortList: sortList,
          setSortType: setSortType,
        }}
      />
      <AssetHistoryContainer>
        <PagingContent
          emptyText="조회 가능한 내역이 없습니다."
          pagingStore={pagingStore}
          loadParam={{ data: { purchaseType: filterType } }}
        >
          {pagingStore.list.map((d, i) => (
            <HistoryContainer key={i}>
              <DateContainer>
                <TextStyle type={'b_12'}>{`${moment(new Date(d.createdDate)).format('YY.MM.DD')}`}</TextStyle>
                <TextStyle type={'r_12'}>{`${moment(new Date(d.createdDate)).format('HH:MM')}`}</TextStyle>
              </DateContainer>
              <ProductInfoContainer onClick={() => onTxDetailModal(pagingStore.list[i] as unknown as HistoryList[])}>
                <div className="product-info">
                  <IonText className="order-sequence">{`주문번호: ${d.orderSequence ?? '-'}`}</IonText>
                  <IonText className="title">{d.product.name ?? '-'}</IonText>
                </div>
                <img
                  src={d.product.imageUri}
                  alt={'img'}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null
                    currentTarget.src = '/img/thumbnail.svg'
                  }}
                />
                <ContentContainer>
                  <CustomSpacer spacing="16px" />
                  {d.purchaseType === 'SALE' && (
                    <>
                      <ContentWrap>
                        <TextStyle type={'b_14'} color={'error_main'}>
                          조각구매
                        </TextStyle>
                        <TextStyle type={'r_13'}>{`단가 ${d.cost.toLocaleString('ko-Kr') ?? 0} 원`}</TextStyle>
                      </ContentWrap>
                      <ContentWrap>
                        <TextStyle type={'r_12'}>{`${Math.abs(d.count).toLocaleString('ko-Kr') ?? 0} 개`}</TextStyle>
                        <TextStyle type={'b_13'}>{`총 ${
                          (Math.abs(d.count) * d.cost).toLocaleString('ko-Kr') ?? 0
                        } 원`}</TextStyle>
                      </ContentWrap>
                    </>
                  )}
                  {d.purchaseType === 'TRADE' && (
                    <>
                      <ContentWrap>
                        {d.tradeOrder?.type === 'BUY' ? (
                          <TextStyle type={'b_14'} color={'error_main'}>
                            트레이드(매수)
                          </TextStyle>
                        ) : (
                          <TextStyle type={'b_14'} color={'secondary_main'}>
                            트레이드(매도)
                          </TextStyle>
                        )}
                        <TextStyle type={'r_13'}>{`단가 ${d.cost.toLocaleString('ko-Kr') ?? 0} 원`}</TextStyle>
                      </ContentWrap>
                      <ContentWrap>
                        <TextStyle type={'r_12'}>{`${Math.abs(d.count).toLocaleString('ko-Kr') ?? 0} 개`}</TextStyle>
                        <TextStyle type={'b_13'}>{`총 ${
                          d.tradeOrder?.type === 'BUY'
                            ? (Math.abs(d.count) * d.cost + d.fee).toLocaleString('ko-Kr') ?? 0
                            : (Math.abs(d.count) * d.cost - d.fee).toLocaleString('ko-Kr') ?? 0
                        } 원`}</TextStyle>
                      </ContentWrap>
                      <ContentWrap>
                        <TextStyle type={'r_12'}></TextStyle>
                        <TextStyle type={'m_12'}>(수수료 포함)</TextStyle>
                      </ContentWrap>
                    </>
                  )}
                  {d.purchaseType === 'WALLET_TRANSFER' && (
                    <ContentWrap>
                      <TextStyle type={'b_14'} color={'primary_light'}>
                        지갑 이전
                      </TextStyle>
                      <TextStyle type={'r_12'}>{`${Math.abs(d.count).toLocaleString('ko-Kr') ?? 0} 개`}</TextStyle>
                    </ContentWrap>
                  )}
                  {d.purchaseType === 'DESTRUCT' && (
                    <ContentWrap>
                      <TextStyle type={'b_14'} color={'error_main'}>
                        조각 소각
                      </TextStyle>
                      <TextStyle type={'r_12'}>{`${Math.abs(d.count).toLocaleString('ko-Kr') ?? 0} 개`}</TextStyle>
                    </ContentWrap>
                  )}
                  {d.purchaseType === 'REFUND' && (
                    <>
                      <ContentWrap>
                        <TextStyle type={'b_14'}>환불</TextStyle>
                        <TextStyle type={'r_13'}>{`단가 ${d.cost.toLocaleString('ko-Kr') ?? 0} 원`}</TextStyle>
                      </ContentWrap>
                      <ContentWrap>
                        <TextStyle type={'r_12'}>{`${Math.abs(d.count).toLocaleString('ko-Kr') ?? 0} 개`}</TextStyle>
                        <TextStyle type={'b_13'}>{`총  ${
                          (Math.abs(d.count) * d.cost).toLocaleString('ko-Kr') ?? 0
                        } 원`}</TextStyle>
                      </ContentWrap>
                    </>
                  )}
                </ContentContainer>
              </ProductInfoContainer>
            </HistoryContainer>
          ))}
        </PagingContent>
      </AssetHistoryContainer>
    </>
  )
})

const AssetHistoryContainer = styled.div`
  padding: 0 20px;
`

const HistoryContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 5fr;
  grid-column-gap: 20px;
  grid-row-gap: 0px;
`

const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0;
`

const ProductInfoContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(2, 1fr);
  grid-template-rows: 2fr;
  grid-column-gap: 20px;
  grid-row-gap: 0px;
  padding: 16px 0;
  border-bottom: solid 1px var(--ion-color-gray100);
  img {
    grid-area: 1 / 5 / 2 / 6;
    border-radius: 7px;
    min-width: 52px;
    width: 100%;
    aspect-ratio: 1/1;
    object-fit: cover;
  }
  .product-info {
    grid-area: 1 / 1 / 2 / 5;
    display: flex;
    flex-direction: column;
    font-size: 15px;
    .order-sequence {
      white-space: nowrap;
      color: var(--ion-color-primary_light);
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .title {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      word-wrap: break-word;
      line-height: 1.2em;
      max-height: 40px;
      font-size: 14px;
    }
  }
`

const ContentContainer = styled.div`
  grid-area: 2 / 1 / 3 / 6;
`

const ContentWrap = styled.div`
  display: flex;
  justify-content: space-between;
`
